import React, { useState, useEffect } from 'react';
import {
  X,
  Edit,
  Trash2,
  Plus,
  CheckCircle,
  CheckSquare,
  Circle,
  Calendar,
  User,
  Tag,
  Paperclip,
  MessageSquare,
  Clock,
  AlertCircle,
  Download,
  Upload,
  Send,
  MoreVertical,
  Loader2,
  Edit2,
  Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card as UICard, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import taskService from '@/services/taskService';

const TaskCardModal = ({ isOpen, onClose, card: initialCard, boardId, onCardUpdated }) => {
  const { toast } = useToast();
  const cardId = initialCard?._id;
  
  // Estados
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [card, setCard] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({});
  
  // Checklist
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [addingChecklist, setAddingChecklist] = useState(false);
  
  // Comentários
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  
  // Upload
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Tabs
  const [activeTab, setActiveTab] = useState('details');
  
  // Listas para integrações
  const [projects, setProjects] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  
  // Lista de usuários para assignee
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Labels
  const [boardLabels, setBoardLabels] = useState([]);
  const [showLabelMenu, setShowLabelMenu] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#3b82f6');
  const [creatingLabel, setCreatingLabel] = useState(false);
  
  // Card Actions
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [allBoards, setAllBoards] = useState([]);
  const [selectedTargetBoard, setSelectedTargetBoard] = useState('');
  const [targetBoardColumns, setTargetBoardColumns] = useState([]);
  const [selectedTargetColumn, setSelectedTargetColumn] = useState('');

  // Carregar dados do card quando modal abre
  useEffect(() => {
    if (isOpen && cardId) {
      console.log('🔵 TaskCardModal: Modal aberto, carregando dados do card:', cardId);
      console.log('🔍 Prop initialCard recebido:', initialCard);
      loadCardData();
      loadIntegrationOptions();
      loadUsers();
    } else if (!isOpen) {
      // Reset state when modal closes
      console.log('🔴 TaskCardModal: Modal fechado, resetando estado');
      setCard(null);
      setActiveTab('details');
      setNewChecklistItem('');
      setNewComment('');
      setShowLabelMenu(false);
    }
  }, [isOpen, cardId]);
  
  // Carregar labels quando o card carregar
  useEffect(() => {
    if (card && card.board_id) {
      loadBoardLabels(card.board_id);
    }
  }, [card?.board_id]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/usuarios`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const usersData = await response.json();
        console.log('👥 Usuários carregados:', usersData);
        // Filtrar apenas técnicos e admins ativos
        const activeUsers = usersData.filter(u => 
          (u.role === 'tecnico' || u.role === 'admin') && u.is_active !== false
        );
        console.log('✅ Usuários ativos (técnicos/admins):', activeUsers);
        setUsers(activeUsers);
      } else {
        console.error('❌ Erro ao buscar usuários:', response.status);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar usuários:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadBoardLabels = async (boardId) => {
    try {
      const labels = await taskService.getBoardLabels(boardId);
      setBoardLabels(labels || []);
    } catch (error) {
      console.error('Erro ao carregar labels:', error);
    }
  };

  const handleCreateLabel = async () => {
    if (!newLabelName.trim() || !card.board_id) return;
    
    setCreatingLabel(true);
    try {
      await taskService.createLabel({
        board_id: card.board_id,
        name: newLabelName.trim(),
        color: newLabelColor
      });
      
      setNewLabelName('');
      setNewLabelColor('#3b82f6');
      await loadBoardLabels(card.board_id);
      
      toast({
        title: 'Label criada!',
        description: 'Nova label adicionada ao board.',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível criar a label.',
        variant: 'destructive',
      });
    } finally {
      setCreatingLabel(false);
    }
  };

  const handleToggleLabel = async (labelName) => {
    const currentLabels = card.labels || [];
    const hasLabel = currentLabels.includes(labelName);
    
    const newLabels = hasLabel
      ? currentLabels.filter(l => l !== labelName)
      : [...currentLabels, labelName];
    
    try {
      await taskService.updateCard(cardId, { labels: newLabels });
      setCard(prev => ({ ...prev, labels: newLabels }));
      
      toast({
        title: hasLabel ? 'Label removida' : 'Label adicionada',
        description: hasLabel ? `"${labelName}" removida do card.` : `"${labelName}" adicionada ao card.`,
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar as labels.',
        variant: 'destructive',
      });
    }
  };

  const handleCopyCard = async () => {
    try {
      const result = await taskService.copyCard(cardId);
      
      toast({
        title: 'Card copiado!',
        description: 'O card foi duplicado com sucesso.',
      });
      
      // Notificar pai para recarregar
      if (onCardUpdated) {
        onCardUpdated();
      }
      
      onClose();
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível copiar o card.',
        variant: 'destructive',
      });
    }
  };

  const openMoveModal = async () => {
    try {
      const boards = await taskService.getBoards({});
      // Filtrar board atual
      setAllBoards(boards.filter(b => b._id !== card.board_id));
      setShowMoveModal(true);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar boards.',
        variant: 'destructive',
      });
    }
  };

  const handleTargetBoardChange = async (boardId) => {
    setSelectedTargetBoard(boardId);
    setSelectedTargetColumn('');
    
    try {
      console.log('🔄 Carregando colunas do board:', boardId);
      const columns = await taskService.getBoardColumns(boardId);
      console.log('✅ Colunas carregadas:', columns);
      setTargetBoardColumns(columns || []);
    } catch (error) {
      console.error('❌ Erro ao carregar colunas:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar colunas.',
        variant: 'destructive',
      });
    }
  };

  const handleMoveToBoard = async () => {
    if (!selectedTargetBoard || !selectedTargetColumn) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Selecione o board e a coluna de destino.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      await taskService.moveCardToBoard(cardId, selectedTargetBoard, selectedTargetColumn);
      
      toast({
        title: 'Card movido!',
        description: 'O card foi movido para outro board.',
      });
      
      // Notificar pai para recarregar
      if (onCardUpdated) {
        onCardUpdated();
      }
      
      onClose();
      setShowMoveModal(false);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível mover o card.',
        variant: 'destructive',
      });
    }
  };

  const loadIntegrationOptions = async () => {
    setLoadingIntegrations(true);
    try {
      // Buscar projetos, tickets e leads em paralelo
      const [projectsRes, ticketsRes, leadsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projetos`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.ok ? r.json() : []).catch(() => []),
        
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chamados`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.ok ? r.json() : []).catch(() => []),
        
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.ok ? r.json() : []).catch(() => [])
      ]);
      
      // Filtrar apenas itens ativos
      // Projetos: excluir "concluido" e "cancelado"
      const activeProjects = (projectsRes || []).filter(p => 
        p.status !== 'concluido' && p.status !== 'cancelado'
      );
      
      // Chamados: excluir "concluido" e "cancelado"
      const activeTickets = (ticketsRes || []).filter(t => 
        t.status !== 'concluido' && t.status !== 'cancelado'
      );
      
      // Leads: excluir "ganho" e "perdido" (leads encerrados)
      const activeLeads = (leadsRes || []).filter(l => 
        l.status !== 'ganho' && l.status !== 'perdido'
      );
      
      setProjects(activeProjects);
      setTickets(activeTickets);
      setLeads(activeLeads);
    } catch (error) {
      console.error('Erro ao carregar opções de integração:', error);
    } finally {
      setLoadingIntegrations(false);
    }
  };

  const loadCardData = async () => {
    setLoading(true);
    try {
      console.log('📥 Carregando dados do card:', cardId);
      
      const cardData = await taskService.getCardById(cardId);
      
      console.log('✅ Dados do card recebidos:', {
        title: cardData.title,
        priority: cardData.priority,
        due_date: cardData.due_date,
        project_id: cardData.project_id,
        lead_id: cardData.lead_id,
        ticket_id: cardData.ticket_id,
        checklists: cardData.checklists?.length || 0,
        comments: cardData.comments?.length || 0,
        attachments: cardData.attachments?.length || 0,
        activities: cardData.activities?.length || 0
      });
      
      // Ensure all arrays exist
      const normalizedCard = {
        ...cardData,
        checklists: cardData.checklists || [],
        comments: cardData.comments || [],
        attachments: cardData.attachments || [],
        activities: cardData.activities || []
      };
      
      setCard(normalizedCard);
      setEditValues({
        title: normalizedCard.title || '',
        description: normalizedCard.description || '',
        priority: normalizedCard.priority || 'media',
        assigned_to: normalizedCard.assigned_to || '',
        due_date: normalizedCard.due_date ? normalizedCard.due_date.split('T')[0] : '',
        estimated_hours: normalizedCard.estimated_hours || '',
        project_id: normalizedCard.project_id || '',
        ticket_id: normalizedCard.ticket_id || '',
        lead_id: normalizedCard.lead_id || ''
      });
    } catch (error) {
      console.error('❌ Erro ao carregar card:', error);
      toast({
        title: 'Erro ao carregar card',
        description: 'Não foi possível carregar os detalhes do card.',
        variant: 'destructive',
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateField = async (field, value) => {
    if (saving) return; // Prevent concurrent updates
    
    setSaving(true);
    try {
      // Preparar valor conforme o campo
      let formattedValue = value;
      
      // Para data de vencimento, garantir formato ISO
      if (field === 'due_date' && value) {
        const date = new Date(value);
        date.setHours(23, 59, 59, 999);
        formattedValue = date.toISOString();
      }
      
      // Para tempo estimado, converter para número
      if (field === 'estimated_hours' && value) {
        formattedValue = parseFloat(value);
      }
      
      console.log('🔄 Atualizando campo:', field, 'de', card[field], 'para', formattedValue);
      
      // Optimistic update - Update UI immediately
      setCard(prev => ({ ...prev, [field]: formattedValue }));
      
      // Send to backend
      const updatedCard = await taskService.updateCard(cardId, { [field]: formattedValue });
      console.log('✅ Campo atualizado no backend:', updatedCard);
      
      // Update with backend response to ensure consistency
      setCard(prev => ({ ...prev, ...updatedCard }));
      
      setEditingField(null);
      
      // Notify parent component if callback exists
      if (onCardUpdated) {
        onCardUpdated(updatedCard);
      }
      
      toast({
        title: 'Atualizado!',
        description: `${field === 'priority' ? 'Prioridade' : field === 'due_date' ? 'Data' : 'Campo'} atualizado com sucesso.`,
      });
    } catch (error) {
      console.error('❌ Erro ao atualizar campo:', error);
      
      // Revert optimistic update on error
      await loadCardData();
      
      toast({
        title: 'Erro ao atualizar',
        description: error.response?.data?.detail || 'Não foi possível atualizar o campo.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddChecklistItem = async () => {
    const trimmedTitle = newChecklistItem.trim();
    
    if (!trimmedTitle) {
      toast({
        title: 'Campo obrigatório',
        description: 'Digite o nome do checklist.',
        variant: 'destructive',
      });
      return;
    }

    setAddingChecklist(true);
    try {
      console.log('🔄 Criando checklist para card:', cardId, 'com título:', trimmedTitle);
      
      const newChecklist = await taskService.createChecklist({
        card_id: cardId,
        title: trimmedTitle,
        items: []
      });
      
      console.log('✅ Checklist criado:', newChecklist);

      // Clear input immediately
      setNewChecklistItem('');
      
      // Optimistic update - Add to local state immediately
      setCard(prev => ({
        ...prev,
        checklists: [...(prev.checklists || []), newChecklist]
      }));

      toast({
        title: 'Checklist criado!',
        description: `"${trimmedTitle}" foi adicionado com sucesso.`,
      });
    } catch (error) {
      console.error('❌ Erro ao adicionar checklist:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Não foi possível adicionar o checklist.',
        variant: 'destructive',
      });
    } finally {
      setAddingChecklist(false);
    }
  };

  const handleToggleChecklistItem = async (checklistId, itemId, checked) => {
    try {
      const checklist = card.checklists.find(c => c._id === checklistId);
      if (!checklist) return;
      
      const updatedItems = checklist.items.map(item =>
        item.id === itemId ? { ...item, checked } : item
      );
      
      console.log('🔄 Toggling checklist item:', itemId, 'checked:', checked);
      
      const response = await taskService.updateChecklist(checklistId, { items: updatedItems });
      console.log('✅ Checklist item toggled:', response);
      
      // Atualizar estado local imediatamente
      setCard(prev => ({
        ...prev,
        checklists: prev.checklists.map(c =>
          c._id === checklistId ? { ...c, items: updatedItems } : c
        )
      }));
    } catch (error) {
      console.error('❌ Erro ao atualizar item:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar o item.',
        variant: 'destructive',
      });
    }
  };

  const handleAddChecklistSubItem = async (checklistId, itemText) => {
    if (!itemText.trim()) return;
    
    try {
      const checklist = card.checklists.find(c => c._id === checklistId);
      if (!checklist) {
        console.error('Checklist not found:', checklistId);
        return;
      }
      
      const newItem = {
        id: Date.now().toString(),
        text: itemText.trim(),
        checked: false
      };
      const updatedItems = [...(checklist.items || []), newItem];
      
      console.log('🔄 Atualizando checklist:', checklistId, 'com items:', updatedItems);
      
      const response = await taskService.updateChecklist(checklistId, { items: updatedItems });
      console.log('✅ Checklist atualizado:', response);
      
      // Atualizar estado local com resposta do backend
      setCard(prev => ({
        ...prev,
        checklists: prev.checklists.map(c =>
          c._id === checklistId ? { ...c, items: updatedItems } : c
        )
      }));
      
      toast({
        title: 'Item adicionado!',
        description: `"${itemText.trim()}" adicionado ao checklist.`,
      });
    } catch (error) {
      console.error('❌ Erro ao adicionar item ao checklist:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível adicionar o item.',
        variant: 'destructive',
      });
    }
  };

  const handleAddComment = async () => {
    const trimmedComment = newComment.trim();
    
    if (!trimmedComment) {
      toast({
        title: 'Campo obrigatório',
        description: 'Digite seu comentário.',
        variant: 'destructive',
      });
      return;
    }

    setSendingComment(true);
    try {
      console.log('🔄 Criando comentário para card:', cardId);
      
      const createdComment = await taskService.createComment({
        card_id: cardId,
        content: trimmedComment  // CHANGED: text -> content
      });
      
      console.log('✅ Comentário criado:', createdComment);

      // Clear input immediately
      setNewComment('');
      
      // Optimistic update - Add to local state immediately
      setCard(prev => ({
        ...prev,
        comments: [...(prev.comments || []), createdComment]
      }));

      toast({
        title: 'Comentário publicado!',
        description: 'Seu comentário foi adicionado.',
      });
    } catch (error) {
      console.error('❌ Erro ao adicionar comentário:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Não foi possível adicionar o comentário.',
        variant: 'destructive',
      });
    } finally {
      setSendingComment(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('📎 Arquivo selecionado:', file.name, file.type, file.size);

    const validation = taskService.validateFile(file);
    if (!validation.valid) {
      toast({
        title: 'Arquivo inválido',
        description: validation.error,
        variant: 'destructive',
      });
      e.target.value = '';
      return;
    }

    setUploadingFile(true);
    try {
      console.log('🔄 Fazendo upload do arquivo:', file.name);
      
      const uploadedAttachment = await taskService.uploadAttachment(cardId, file);
      console.log('✅ Arquivo enviado:', uploadedAttachment);

      // Optimistic update - Add to local state immediately
      setCard(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), uploadedAttachment]
      }));

      toast({
        title: 'Arquivo enviado!',
        description: `"${file.name}" foi anexado com sucesso.`,
      });
    } catch (error) {
      console.error('❌ Erro no upload:', error);
      toast({
        title: 'Erro no upload',
        description: error.response?.data?.detail || 'Não foi possível enviar o arquivo.',
        variant: 'destructive',
      });
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const calculateChecklistProgress = () => {
    if (!card?.checklists || card.checklists.length === 0) return 0;
    
    let totalItems = 0;
    let checkedItems = 0;
    
    card.checklists.forEach(checklist => {
      totalItems += checklist.items?.length || 0;
      checkedItems += checklist.items?.filter(item => item.checked).length || 0;
    });
    
    return totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;
  };

  if (!card && !loading) return null;

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : card ? (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <DialogTitle className="text-2xl pr-8">
                    {card.title}
                  </DialogTitle>
                  {/* Labels aplicadas */}
                  {card.labels && card.labels.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {card.labels.map((labelName) => {
                        const label = boardLabels.find(l => l.name === labelName);
                        return (
                          <span
                            key={labelName}
                            className="px-3 py-1 rounded text-xs font-medium text-white"
                            style={{ backgroundColor: label?.color || '#6b7280' }}
                          >
                            {labelName}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleCopyCard}>
                        <Plus className="h-4 w-4 mr-2" />
                        Copiar Card
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={openMoveModal}>
                        <Edit className="h-4 w-4 mr-2" />
                        Mover para Outro Board
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="sm" onClick={onClose}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-6 mt-4">
              {/* Quick Actions */}
              <div className="flex items-center gap-3 flex-wrap">
                <Select
                  value={card.priority || 'media'}
                  onValueChange={(value) => handleUpdateField('priority', value)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Prioridade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <Input
                    type="date"
                    value={card.due_date ? card.due_date.split('T')[0] : ''}
                    onChange={(e) => handleUpdateField('due_date', e.target.value)}
                    className="w-40"
                  />
                </div>

                {/* Assignee Selection */}
                <Select
                  value={card.assigned_to || 'none'}
                  onValueChange={(value) => {
                    const newValue = value === 'none' ? null : value;
                    handleUpdateField('assigned_to', newValue);
                  }}
                  disabled={loadingUsers}
                >
                  <SelectTrigger className="w-52">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" />
                      <SelectValue placeholder="Não atribuído" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-gray-500">Não atribuído</span>
                    </SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user._id} value={user._id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Labels */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowLabelMenu(!showLabelMenu)}
                    className="flex items-center gap-2"
                  >
                    <Tag className="h-4 w-4" />
                    <span>Labels</span>
                    {card.labels && card.labels.length > 0 && (
                      <span className="ml-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                        {card.labels.length}
                      </span>
                    )}
                  </Button>
                  
                  {showLabelMenu && (
                    <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-xl border z-50">
                      <div className="p-3 border-b">
                        <h4 className="font-medium text-sm mb-2">Labels do Board</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {boardLabels.length === 0 ? (
                            <p className="text-xs text-gray-500">Nenhuma label criada</p>
                          ) : (
                            boardLabels.map((label) => (
                              <div
                                key={label._id}
                                onClick={() => handleToggleLabel(label.name)}
                                className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={card.labels?.includes(label.name)}
                                  onChange={() => {}}
                                  className="cursor-pointer"
                                />
                                <span
                                  className="px-3 py-1 rounded text-xs font-medium text-white flex-1"
                                  style={{ backgroundColor: label.color }}
                                >
                                  {label.name}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                      
                      <div className="p-3">
                        <h4 className="font-medium text-sm mb-2">Criar Nova Label</h4>
                        <div className="flex gap-2 mb-2">
                          <Input
                            value={newLabelName}
                            onChange={(e) => setNewLabelName(e.target.value)}
                            placeholder="Nome da label"
                            className="text-sm"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleCreateLabel();
                            }}
                          />
                          <input
                            type="color"
                            value={newLabelColor}
                            onChange={(e) => setNewLabelColor(e.target.value)}
                            className="w-12 h-9 rounded border cursor-pointer"
                          />
                        </div>
                        <Button
                          size="sm"
                          onClick={handleCreateLabel}
                          disabled={!newLabelName.trim() || creatingLabel}
                          className="w-full"
                        >
                          {creatingLabel ? 'Criando...' : 'Criar Label'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {card.checklists && card.checklists.length > 0 && (
                  <div className="flex items-center gap-2">
                    <CheckSquare className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {calculateChecklistProgress()}% concluído
                    </span>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {card.checklists && card.checklists.length > 0 && (
                <Progress value={calculateChecklistProgress()} className="h-2" />
              )}

              {/* Tabs */}
              <div className="border-b">
                <div className="flex gap-4">
                  <button
                    className={`pb-2 px-1 ${activeTab === 'details' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
                    onClick={() => setActiveTab('details')}
                  >
                    Detalhes
                  </button>
                  <button
                    className={`pb-2 px-1 flex items-center gap-1 ${activeTab === 'checklists' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
                    onClick={() => setActiveTab('checklists')}
                  >
                    <CheckSquare className="h-4 w-4" />
                    Checklists ({card.checklists?.length || 0})
                  </button>
                  <button
                    className={`pb-2 px-1 flex items-center gap-1 ${activeTab === 'comments' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
                    onClick={() => setActiveTab('comments')}
                  >
                    <MessageSquare className="h-4 w-4" />
                    Comentários ({card.comments?.length || 0})
                  </button>
                  <button
                    className={`pb-2 px-1 flex items-center gap-1 ${activeTab === 'attachments' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
                    onClick={() => setActiveTab('attachments')}
                  >
                    <Paperclip className="h-4 w-4" />
                    Anexos ({card.attachments?.length || 0})
                  </button>
                  <button
                    className={`pb-2 px-1 flex items-center gap-1 ${activeTab === 'activity' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
                    onClick={() => setActiveTab('activity')}
                  >
                    <Clock className="h-4 w-4" />
                    Atividade
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="min-h-[300px]">
                {activeTab === 'details' && (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Descrição</Label>
                      <Textarea
                        value={editValues.description}
                        onChange={(e) => {
                          setEditValues(prev => ({ ...prev, description: e.target.value }));
                        }}
                        onBlur={(e) => {
                          const newValue = e.target.value;
                          const currentValue = card.description || '';
                          if (newValue !== currentValue) {
                            handleUpdateField('description', newValue);
                          }
                        }}
                        rows={5}
                        placeholder="Adicione uma descrição detalhada..."
                      />
                    </div>

                    <Separator />

                    <div>
                      <Label className="text-sm font-medium mb-2 block">Integrações</Label>
                      {loadingIntegrations ? (
                        <p className="text-sm text-gray-500">Carregando opções...</p>
                      ) : (
                        <div className="space-y-3">
                          {/* Projeto */}
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">Projeto</Label>
                            <Select
                              value={card.project_id || 'none'}
                              onValueChange={(value) => {
                                console.log('🔄 Projeto selecionado:', value);
                                const newValue = value === 'none' ? null : value;
                                console.log('🔄 Atualizando project_id para:', newValue);
                                handleUpdateField('project_id', newValue);
                              }}
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue placeholder="Nenhum projeto vinculado" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Nenhum projeto</SelectItem>
                                {projects.map((project) => (
                                  <SelectItem key={project._id} value={project._id}>
                                    {project.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Ticket */}
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">Chamado</Label>
                            <Select
                              value={card.ticket_id || 'none'}
                              onValueChange={(value) => {
                                const newValue = value === 'none' ? null : value;
                                handleUpdateField('ticket_id', newValue);
                              }}
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue placeholder="Nenhum chamado vinculado" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Nenhum chamado</SelectItem>
                                {tickets.map((ticket) => (
                                  <SelectItem key={ticket._id} value={ticket._id}>
                                    #{ticket.ticket_number} - {ticket.title}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Lead */}
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">Lead (CRM)</Label>
                            <Select
                              value={card.lead_id || 'none'}
                              onValueChange={(value) => {
                                console.log('🔄 Lead selecionado:', value);
                                const newValue = value === 'none' ? null : value;
                                console.log('🔄 Atualizando lead_id para:', newValue);
                                handleUpdateField('lead_id', newValue);
                              }}
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue placeholder="Nenhum lead vinculado" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Nenhum lead</SelectItem>
                                {leads.map((lead) => (
                                  <SelectItem key={lead._id} value={lead._id}>
                                    {lead.name} - {lead.company}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'checklists' && (
                  <div className="space-y-4">
                    {card.checklists && card.checklists.map((checklist) => (
                      <ChecklistSection
                        key={checklist._id}
                        checklist={checklist}
                        onToggleItem={handleToggleChecklistItem}
                        onAddItem={handleAddChecklistSubItem}
                      />
                    ))}
                    
                    <div className="flex gap-2">
                      <Input
                        value={newChecklistItem}
                        onChange={(e) => setNewChecklistItem(e.target.value)}
                        placeholder="Nome do novo checklist..."
                        onKeyPress={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                      />
                      <Button onClick={handleAddChecklistItem} disabled={addingChecklist}>
                        {addingChecklist ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                )}

                {activeTab === 'comments' && (
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Adicionar um comentário..."
                        rows={3}
                      />
                      <Button
                        onClick={handleAddComment}
                        disabled={sendingComment || !newComment.trim()}
                      >
                        {sendingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>

                    <Separator />

                    <div className="space-y-3">
                      {card.comments && card.comments.length > 0 ? (
                        card.comments.map((comment) => (
                          <div key={comment._id} className="bg-gray-50 p-3 rounded">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-sm">
                                {comment.user_name || 'Usuário'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(comment.created_at).toLocaleString('pt-BR')}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{comment.content}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-gray-500 py-8">
                          Nenhum comentário ainda
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'attachments' && (
                  <div className="space-y-4">
                    <div>
                      <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={uploadingFile}
                      />
                      <Button
                        onClick={() => document.getElementById('file-upload').click()}
                        disabled={uploadingFile}
                        className="w-full"
                      >
                        {uploadingFile ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Anexar Arquivo
                          </>
                        )}
                      </Button>
                    </div>

                    <Separator />

                    <div className="space-y-2">
                      {card.attachments && card.attachments.length > 0 ? (
                        card.attachments.map((attachment) => (
                          <div
                            key={attachment._id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100"
                          >
                            <div className="flex items-center gap-2">
                              <Paperclip className="h-4 w-4 text-gray-500" />
                              <div>
                                <p className="text-sm font-medium">{attachment.file_name}</p>
                                <p className="text-xs text-gray-500">
                                  {taskService.formatFileSize(attachment.file_size)}
                                </p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => taskService.downloadAttachment(attachment._id, attachment.file_name)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-gray-500 py-8">
                          Nenhum anexo
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'activity' && (
                  <div className="space-y-2">
                    {card.activities && card.activities.length > 0 ? (
                      card.activities.slice(0, 10).map((activity) => (
                        <div key={activity._id} className="flex items-start gap-3 p-2 border-l-2 border-blue-200">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <User className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm">
                              <span className="font-medium">{activity.user_name || 'Sistema'}</span>
                              {' '}
                              <span className="text-gray-600">{activity.description}</span>
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(activity.created_at).toLocaleString('pt-BR')}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 py-8">
                        Nenhuma atividade registrada
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
    
    {/* Modal de Mover para Outro Board */}
    <MoveCardModal
      isOpen={showMoveModal}
      onClose={() => setShowMoveModal(false)}
      boards={allBoards}
      columns={targetBoardColumns}
      selectedBoard={selectedTargetBoard}
      selectedColumn={selectedTargetColumn}
      onBoardChange={handleTargetBoardChange}
      onColumnChange={setSelectedTargetColumn}
      onMove={handleMoveToBoard}
    />
  </>
  );
};

// Componente ChecklistSection
const ChecklistSection = ({ checklist, onToggleItem, onAddItem }) => {
  const [newItemText, setNewItemText] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);

  const progress = checklist.items?.length > 0
    ? Math.round((checklist.items.filter(item => item.checked).length / checklist.items.length) * 100)
    : 0;

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">{checklist.title}</h4>
        <span className="text-sm text-gray-600">{progress}%</span>
      </div>
      <Progress value={progress} className="h-2" />
      
      <div className="space-y-2">
        {checklist.items?.map((item) => (
          <div key={item.id} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={item.checked}
              onChange={(e) => onToggleItem(checklist._id, item.id, e.target.checked)}
              className="h-4 w-4 rounded"
            />
            <span className={`text-sm ${item.checked ? 'line-through text-gray-500' : ''}`}>
              {item.text}
            </span>
          </div>
        ))}
      </div>

      {showAddItem ? (
        <div className="flex gap-2">
          <Input
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            placeholder="Nome do item..."
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                onAddItem(checklist._id, newItemText);
                setNewItemText('');
                setShowAddItem(false);
              }
            }}
          />
          <Button
            size="sm"
            onClick={() => {
              onAddItem(checklist._id, newItemText);
              setNewItemText('');
              setShowAddItem(false);
            }}
          >
            Adicionar
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAddItem(true)}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Adicionar item
        </Button>
      )}
    </div>
  );
};

// Modal de Mover para Outro Board
const MoveCardModal = ({ isOpen, onClose, boards, columns, selectedBoard, selectedColumn, onBoardChange, onColumnChange, onMove }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mover Card para Outro Board</DialogTitle>
          <DialogDescription>
            Selecione o board e a coluna de destino
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Board de Destino</Label>
            <Select value={selectedBoard} onValueChange={onBoardChange}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um board" />
              </SelectTrigger>
              <SelectContent>
                {boards.map((board) => (
                  <SelectItem key={board._id} value={board._id}>
                    {board.icon} {board.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedBoard && (
            <div className="space-y-2">
              <Label>Coluna de Destino</Label>
              <Select value={selectedColumn} onValueChange={onColumnChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma coluna" />
                </SelectTrigger>
                <SelectContent>
                  {columns.map((column) => (
                    <SelectItem key={column._id} value={column._id}>
                      {column.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={onMove} disabled={!selectedBoard || !selectedColumn}>
            Mover Card
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskCardModal;
